<template>
  <div class="barplot-chart-container">
    <h4 v-if="title">{{ title }}</h4>
    <div class="barplot-chart"></div>
    <div class="traveltime-legend">
      {{ $t('report.travelTimeLegende', { sensitiveCaseDuration: sensitiveCaseDuration }) }}
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: {
    title: {
      type: String,
    },

    chartWidth: {
      type: Number,
      default: 1780,
    },

    chartHeight: {
      type: Number,
      default: 1780,
    },

    chartMargin: {
      type: Object,

      default: () => {
        return { top: 32, right: 48, bottom: 64, left: 48 };
      },
    },

    data: {
      type: Array,
      required: true,
    },

    advencedMode: {
      type: Boolean,
      default: false,
    },

    index: {
      type: Number,
      default: -1,
    },

    sensitiveCaseDuration: {
      type: Number,
      required: true,
    },

    useExtendedTimeRanges: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      margin: this.chartMargin,
      width: (this.chartWidth || 1780) - this.chartMargin.left - this.chartMargin.right,
      height: (this.chartHeight || 754) - this.chartMargin.top - this.chartMargin.bottom,
      xScale: null,
      yScale: null,
      xAxis: null,
      yAxis: null,
      duration: 700,
      svg: null,
      color: null,
    };
  },

  computed: {
    colors() {
      const extraColors = this.useExtendedTimeRanges ? ['#611403'] : [];

      return this.advencedMode
        ? ['#2bcbba', '#1daea1', '#20bf6b', '#fed330', '#f5a623', '#fa8231', '#d60808', ...extraColors]
        : ['#1daea1', '#f5a623', '#d60808', ...extraColors];
    },

    ranges() {
      const extraRanges = this.useExtendedTimeRanges ? ['r61120', 'r120'] : ['r60'];

      return this.advencedMode ? ['r010', 'r1120', 'r2130', 'r3140', 'r4150', 'r5160', ...extraRanges] : ['r030', 'r3160', ...extraRanges];
    },
  },

  watch: {
    data: {
      handler() {
        this.updateGraph();
      },

      deep: true,
    },

    advencedMode() {
      this.updateGraph();
    },

    index() {
      this.updateGraph();
    },
  },

  mounted() {
    this.drawGraph();
    this.drawLegend();
  },

  methods: {
    drawGraph() {
      const { margin, width, height } = this;

      this.svg = d3
        .select(this.$el.querySelector('.barplot-chart'))
        .append('svg')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

      const series = d3.stack().keys(this.ranges)(this.data);

      this.xScale = d3
        .scaleBand()
        .domain(this.data.map(d => d.site))
        .range([0, this.width])
        .padding(0.2);

      this.yScale = d3
        .scaleLinear()
        .domain([0, d3.max(series, d => d3.max(d, d => d[1]))])
        .range([this.height, 0]);

      const { xScale, yScale } = this;
      const color = d3.scaleOrdinal(this.colors);

      const rects = this.svg
        .selectAll('g')
        .data(series)
        .enter()
        .append('g')
        .attr('fill', d => color(d.key));

      rects
        .selectAll('rect')
        .data(d => d)
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('x', d => xScale(d.data.site))
        .attr('y', d => yScale(d[1]))
        .attr('height', d => yScale(d[0]) - yScale(d[1]))
        .attr('width', xScale.bandwidth())
        .style('opacity', (d, i) => (this.index === -1 || i === this.index + 1 ? 1 : 0.2));

      const texts = this.svg.selectAll('.text').data(series).enter().append('g');

      texts
        .selectAll('.text')
        .data(d => d)
        .enter()
        .append('text')
        .attr('class', 'text')
        .attr('x', d => xScale(d.data.site) + xScale.bandwidth() / 2)
        .attr('y', d => yScale(d[0]) + (yScale(d[1]) - yScale(d[0])) / 2 + 8)
        .text(d => {
          const value = d[1] - d[0];
          const total = d.data.total;

          const percent = Math.ceil((value / total) * 100);

          return percent > 5 ? `${percent}%` : '';
        })
        .style('opacity', (d, i) => (this.index === -1 || i === this.index + 1 ? 1 : 0.2))
        .attr('font-family', 'Helvetica')
        .style('text-anchor', 'middle')
        .style('pointer-events', 'none')
        .attr('font-size', '16px')
        .attr('fill', 'black');

      this.xAxis = this.svg
        .append('g')
        .attr('id', 'xAxis')
        .attr('transform', `translate(0, ${this.height})`)
        .call(d3.axisBottom(xScale))
        .selectAll('text')
        .attr('dy', (_, i) => (i % 2 === 1 ? '20px' : ''));

      this.yAxis = this.svg.append('g').attr('id', 'yAxis').call(d3.axisLeft(yScale));

      this.yAxis
        .append('g')
        .append('text')
        .attr('class', 'y-lengend')
        .attr('transform', 'rotate(-90)')
        .attr('y', 2)
        .attr('dy', '1em')
        .attr('text-anchor', 'end')
        .attr('fill', '#3C3C3B')
        .text(this.$t('parameters.totalCollaborators', 100));

      let curveData = [];
      this.data.forEach(e => {
        e.start = true;
        curveData.push({ ...e });
        e.start = false;
        curveData.push({ ...e });
      });

      const pathData = d3
        .line()
        .x(d => (d.start ? xScale(d.site) : xScale(d.site) + xScale.bandwidth()))
        .y(d => yScale(d.t45))
        .curve(d3.curveLinear)(curveData);

      this.svg
        .append('path')
        .attr('d', pathData)
        .attr('class', 'line')
        .attr('fill', 'none')
        .attr('stroke', '#d60808')
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .attr('stroke-width', 3)
        .attr('stroke-opacity', 0.6)
        .style('pointer-events', 'none');
    },

    drawLegend() {
      let legend = this.svg
        .selectAll('.legend')
        .data(this.colors)
        .enter()
        .append('g')
        .attr('class', 'legend')
        .attr('transform', (d, i) => 'translate(30,' + i * 19 + ')');

      legend
        .append('rect')
        .attr('x', this.width - 18)
        .attr('width', 18)
        .attr('height', 18)
        .style('fill', (d, i) => [...this.colors].reverse()[i]);

      legend
        .append('text')
        .attr('x', this.width + 5)
        .attr('y', 9)
        .attr('dy', '.35em')
        .style('text-anchor', 'start')
        .text((d, i) => {
          if (this.useExtendedTimeRanges) {
            if (this.advencedMode) {
              switch (i) {
                case 7:
                  return '0 - 10 min';
                case 6:
                  return '11 - 20 min';
                case 5:
                  return '21 - 30 min';
                case 4:
                  return '31 - 40 min';
                case 3:
                  return '41 - 50 min';
                case 2:
                  return '51 - 60 min';
                case 1:
                  return '61 - 120 min';
                case 0:
                  return '+ 120 min';
              }
            } else {
              switch (i) {
                case 3:
                  return '0 - 30 min';
                case 2:
                  return '31 - 60 min';
                case 1:
                  return '61 - 120 min';
                case 0:
                  return '+ 120 min';
              }
            }
          }

          if (this.advencedMode) {
            switch (i) {
              case 6:
                return '0 - 10 min';
              case 5:
                return '11 - 20 min';
              case 4:
                return '21 - 30 min';
              case 3:
                return '31 - 40 min';
              case 2:
                return '41 - 50 min';
              case 1:
                return '51 - 60 min';
              case 0:
                return '+ 60 min';
            }
          } else {
            switch (i) {
              case 2:
                return '0 - 30 min';
              case 1:
                return '31 - 60 min';
              case 0:
                return '+ 60 min';
            }
          }
        });

      let lineLegend = this.svg
        .append('g')
        .attr('class', 'legend')
        .attr('transform', `translate(30, ${(this.advencedMode ? 7 : 4) * 24})`);

      lineLegend
        .append('path')
        .attr(
          'd',
          d3.line()([
            [this.width - 18, 18],
            [this.width, 0],
          ]),
        )
        .attr('fill', 'none')
        .attr('stroke', '#d60808')
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .attr('stroke-width', 3)
        .attr('stroke-opacity', 0.6);

      lineLegend
        .append('text')
        .attr('x', this.width + 5)
        .attr('y', 9)
        .attr('dy', '.35em')
        .style('text-anchor', 'start')
        .text(`${this.sensitiveCaseDuration} min*`);
    },

    updateGraph() {
      d3.select('svg').remove();

      this.drawGraph();
      this.drawLegend();
    },

    _updateGraph() {
      const { xScale, yScale } = this;

      // const series = d3.stack().keys(this.ranges)(this.data);

      // const rects = this.svg.selectAll("g").data(series);

      // console.log(this.svg.selectAll(".bar"));

      this.svg.selectAll('.bar');
      //   .data(d => d + 5)
      //   .enter()
      //   .transition()
      //   .duration(750)
      //   .attr("x", (d, i) => xScale(d.data.site))
      //   .attr("y", d => yScale(d[1]))
      //   .attr("height", d => yScale(d[0]) - yScale(d[1]))
      //   .attr("width", xScale.bandwidth())

      // this.svg.select('.axis-x')
      //   .transition()
      //   .duration(this.duration)
      //   .call(this.xAxis);

      // this.xAxis.domain(d3.extent(data, function(d) { return d.date; }));

      // this.xScale = this.xScale.domain(d3.extent(this.data.map(d => d.site), d => d));

      const pathData = d3
        .line()
        .x(d => xScale(d.site) + xScale.bandwidth() / 2)
        .y(d => yScale(d.r030 + d.r3160 / 2))
        .curve(d3.curveCardinal)(this.data);

      this.svg.select('.line').transition().duration(this.duration).attr('d', pathData);
    },
  },
};
</script>

<style lang="scss">
@use '@/styles/colors.scss';

svg {
  overflow: visible;
}

.tooltip {
  rect {
    fill: rgba(white, 0.8);
    stroke: black;
    padding: 8px 4px;
    border-radius: 4px;
  }
}

.domain,
.tick,
.tick line {
  stroke: colors.$earth-color;
}

.tick text {
  stroke: none;
  fill: colors.$white-color;
  font-size: 22px;
  transform: translateY(20px);
  font-family: 'Source Sans Pro', Helvetica, sans-serif;
}

.y-lengend {
  font-family: 'Source Sans Pro', Helvetica, sans-serif;
}

.traveltime-legend {
  padding: 0 48px 24px;
  margin: 16px 0 0;
  opacity: 0.6;
}
</style>
